// A tiny Zod-like validator utility tailored for the Edit Profile sections.
// Exports one main function: validateSections(sections)
// It returns { valid: boolean, errors: Array<{id, field, messages: string[]}> }

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const allowedImageTypes = ["image/png", "image/jpg", "image/jpeg"];
const allowedDocTypes = ["image/png", "image/jpg", "image/jpeg"];

function validateSupportiveDocument(document, fileUrl) {
  // Allow if file URL exists (already uploaded) or if new file is provided
  if (!document && !fileUrl) return ["Supportive document is required"];
  const msgs = [];
  if (document && document.size && document.size > MAX_FILE_SIZE) msgs.push("Document must be <= 2MB");
  if (document && document.type && !allowedImageTypes.includes(document.type)) msgs.push("Unsupported file type. Only PNG, JPG, and JPEG are allowed");
  return msgs;
}

function isEmptyString(v) {
  return v == null || String(v).trim() === "";
}

function validateName(values, categoryType) {
  const msgs = [];
  if (isEmptyString(values.first_name)) msgs.push("First name is required");
  else if (/\d/.test(values.first_name)) msgs.push("First name should not contain numbers");

  if (!isEmptyString(values.middle_name) && /\d/.test(values.middle_name)) {
    msgs.push("Middle name should not contain numbers");
  }

  if (!isEmptyString(values.last_name) && /\d/.test(values.last_name)) {
    msgs.push("Last name should not contain numbers");
  }

  // Validate supportive document - ONLY for Support Staff (category '2')
  // or generally if we want to enforce it for everyone except athletes who might not have upload
  if (String(categoryType) === '2') {
    const docMsgs = validateSupportiveDocument(
      values.file || values.name_document || values.name_image,
      values.fileUrl || values.name_image_url
    );
    msgs.push(...docMsgs);
  }
  
  return msgs;
}

function validateDob(values) {
  const msgs = [];
  if (isEmptyString(values.dob)) msgs.push("DOB is required");
  else {
    // accept DD-MM-YYYY or YYYY-MM-DD roughly
    const ddmmyyyy = /^\d{2}-\d{2}-\d{4}$/;
    const iso = /^\d{4}-\d{2}-\d{2}$/;
    if (!ddmmyyyy.test(values.dob) && !iso.test(values.dob))
      msgs.push("DOB should be in DD-MM-YYYY or YYYY-MM-DD format");
  }
  return msgs;
}

function validateAddress(values) {
  const msgs = [];
  if (isEmptyString(values.pincode)) msgs.push("Pincode is required");
  else if (!/^\d{6}$/.test(String(values.pincode))) msgs.push("Pincode must be a 6 digit number");
  
  if (isEmptyString(values.district)) msgs.push("District is required");
  
  // Validate supportive document
  const docMsgs = validateSupportiveDocument(values.file || values.address_document, values.fileUrl);
  msgs.push(...docMsgs);
  
  return msgs;
}

function validateBank(values) {
  const msgs = [];
  if (isEmptyString(values.bank_name)) msgs.push("Bank name is required");
  if (isEmptyString(values.account_holder_name)) msgs.push("Account holder name is required");
  if (isEmptyString(values.account_number)) msgs.push("Account number is required");
  if (isEmptyString(values.branch_name)) msgs.push("Branch name is required");
  else if (!/^\d{6,20}$/.test(String(values.account_number)))
    msgs.push("Account number should be numeric (6-20 digits)");
  if (isEmptyString(values.ifsc_code)) msgs.push("IFSC code is required");
  
  // Validate cancelled cheque or passbook document - check file and fileUrl
  const docMsgs = validateSupportiveDocument(
    values.file || values.cancelled_cheque_or_passbook, 
    values.fileUrl
  );
  msgs.push(...docMsgs);
  
  return msgs;
}

function validateProfile(section) {
  const msgs = [];
  const values = section.values || {};
  
  // supportive document for profile - check both file and fileUrl
  // For profile changes, we expect a new file upload (photofile) or a preview URL if already handled
  const supportive = values.photofile || values.profile_document || values.file || null;
  const docUrl = values.photofileUrl || values.fileUrl;

  const docMsgs = validateSupportiveDocument(supportive, docUrl);
  msgs.push(...docMsgs);

  return msgs;
}

function validateKitOrShoe(values, label) {
  const msgs = [];
  if (isEmptyString(values[label])) msgs.push(`${label.replace(/_/g, ' ')} is required`);
  return msgs;
}

function validateIdentification(values) {
  const msgs = [];
  if (isEmptyString(values.identification_type)) msgs.push("Identification type is required");
  if (isEmptyString(values.identification_number)) msgs.push("Identification number is required");

  // Validate supportive document
  const docMsgs = validateSupportiveDocument(values.file || values.identification_document, values.fileUrl);
  msgs.push(...docMsgs);

  return msgs;
}

function validateSportsAchievement(values) {
  const msgs = [];
  const docMsgs = validateSupportiveDocument(values.file || values.sports_achievement, values.fileUrl);
  msgs.push(...docMsgs);
  return msgs;
}

function validateSport(values, categoryType) {
  const msgs = [];
  
  // Check if sport is empty or "0" (All Sports)
  if (isEmptyString(values.sport) || String(values.sport) === "0") {
    msgs.push("Please select a specific sport");
  }
  
  // Document is now mandatory for all users (support staff and athletes)
  const docMsgs = validateSupportiveDocument(values.file || values.sport_document, values.fileUrl);
  msgs.push(...docMsgs);
  
  return msgs;
}

function validateMaritalStatus(values) {
  const msgs = [];
  if (isEmptyString(values.marital_status)) msgs.push("Marital Status is required");
  return msgs;
}

function validateEducation(values) {
  const msgs = [];
  if (isEmptyString(values.highest_qualification)) {
    msgs.push("Highest Qualification is required");
  } else {
    // Validate that a document is uploaded for the selected qualification
    const qual = values.highest_qualification;
    const uploadEntry = values.education_uploads?.[qual];
    
    // Check if we have a success flag, or a docId, or a file object, or a url
    const hasFile = uploadEntry && (uploadEntry.success || uploadEntry.docId || uploadEntry.doc_file || uploadEntry.fileUrl);
    
    if (!hasFile) {
      msgs.push("Education Certificate is required for the selected qualification");
    }
  }
  return msgs;
}

function validateCenterDetails(values, categoryType) {
  const msgs = [];
  const isSupportStaff = String(categoryType) === '2';
  
  if (isEmptyString(values.district)) msgs.push("District is required");
  
  if (!isSupportStaff) {
    if (isEmptyString(values.center_type)) msgs.push("Center Type is required");
    if (isEmptyString(values.center_name)) msgs.push("Center Name is required");
  }
  
  // Center Details allows upload.
  const docMsgs = validateSupportiveDocument(values.file || values.center_doc, values.fileUrl);
  msgs.push(...docMsgs);

  return msgs;
}

function validateCenterName(values) {
  const msgs = [];
  if (isEmptyString(values.center_name)) {
    msgs.push("Center Name is required");
  } else {
    if (/\d/.test(values.center_name)) {
      msgs.push("Center Name should not contain numbers");
    }
    if (values.center_name.length < 3) {
      msgs.push("Min 3 characters required");
    }
  }
  return msgs;
}

function validateContactPerson(values) {
  const msgs = [];
  if (isEmptyString(values.contact_person)) {
    msgs.push("Contact Person Name is required");
  } else {
    if (/\d/.test(values.contact_person)) {
      msgs.push("Contact Person Name should not contain numbers");
    }
    if (values.contact_person.length < 3) {
      msgs.push("Min 3 characters required");
    }
  }
  
  const docMsgs = validateSupportiveDocument(values.file || values.center_contact_person_name_image, values.fileUrl);
  msgs.push(...docMsgs);
  return msgs;
}

function validateCenterType(values) {
  const msgs = [];
  if (isEmptyString(values.center_type)) msgs.push("Center Type is required");
  return msgs;
}

function validateCenterDistrict(values) {
  const msgs = [];
  if (isEmptyString(values.district)) msgs.push("Center District is required");
  return msgs;
}



export function validateSections(sections, categoryType) {

  console.log("sections", sections);
  // return shape: { valid: boolean, errorsById: { [id]: { field, messages: [] } } }
  const errorsById = {};
  sections.forEach((s) => {
    if (!s.field) return; // skip unselected
    const field = s.field;
    let msgs = [];
    switch (field) {
      case "name":
        msgs = validateName(s.values || {}, categoryType);
        break;
      case "dob":
        msgs = validateDob(s.values || {});
        break;
      case "address":
        msgs = validateAddress(s.values || {});
        break;
      case "photo":
      case "profile":
        msgs = validateProfile(s);
        break;
      case "kit_size":
        msgs = validateKitOrShoe(s.values || {}, "kit_size");
        break;
      case "shoe_size":
        msgs = validateKitOrShoe(s.values || {}, "shoe_size");
        break;
      case "bank_details":
        msgs = validateBank(s.values || {});
        break;
      case "identification":
        msgs = validateIdentification(s.values || {});
        break;
      case "sports_certificate":
      case "sports_achievement":
        msgs = validateSportsAchievement(s.values || {});
        break;
      case "sport":
        msgs = validateSport(s.values || {}, categoryType);
        break;
      case "marital_status":
        msgs = validateMaritalStatus(s.values || {});
        break;
      case "educational_details":
        msgs = validateEducation(s.values || {});
        break;
      case "center_details":
        msgs = validateCenterDetails(s.values || {}, categoryType);
        break;
      case "center_name":
        msgs = validateCenterName(s.values || {});
        break;
      case "contact_person":
        msgs = validateContactPerson(s.values || {});
        break;
      case "center_type":
        msgs = validateCenterType(s.values || {});
        break;
      case "center_district":
        msgs = validateCenterDistrict(s.values || {});
        break;
      default:
        msgs = [];
    }
    if (msgs.length) errorsById[s.id] = { field: s.field, messages: msgs };
  });
  return { valid: Object.keys(errorsById).length === 0, errorsById };
}

export default { validateSections };